import Vue from "vue";
import PersonalDataStore from "./PersonalDataStore.js";
import ObservationDataStore from "./ObservationDataStore.js";
import DocumentDataStore from "./DocumentDataStore.ts";
import AvalancheStore from "./AvalancheStore.js";
import {
  AvalancheMoisture,
  AvalancheSize,
  AvalancheType,
  TriggerType,
} from "@/services/rest/publicreply";

export function createStore() {
  return Vue.observable({
    AvalancheData: new AvalancheStore(),
    PersonalData: new PersonalDataStore(),
    ObservationData: new ObservationDataStore(),
    DocumentData: new DocumentDataStore(),
    validMail: null,
    validComment: true,
    validTotalFileSize: true,
    date: null,
    cancelAlert: false,
    coordinates: null,
    sending: false,
    setLocalStorage(localStorage) {
      this.localStorage = localStorage;
    },
    isLocalStorage() {
      return this.localStorage;
    },
    clear() {
      this.ObservationData = new ObservationDataStore();
      this.AvalancheData = new AvalancheStore();
      this.DocumentData = new DocumentDataStore();
    },
    isValidMail() {
      return this.validMail;
    },
    setValidMail(validMail) {
      this.validMail = validMail;
    },
    setValidComment(validComment) {
      this.validComment = validComment;
    },
    isValidComment() {
      return this.validComment;
    },
    setValidTotalFileSize(validTotalFileSize) {
      this.validTotalFileSize = validTotalFileSize;
    },
    isValidTotalFileSize() {
      return this.validTotalFileSize;
    },
    // eslint-disable-next-line complexity
    isValid() {
      return (
        this.isValidMail() !== false &&
        this.coordinates &&
        this.date &&
        this.isValidComment() &&
        this.isValidTotalFileSize() &&
        this.ObservationData.isAspectValid() &&
        this.ObservationData.isAltitudeValid()
      );
    },
    getDate() {
      return this.date;
    },
    setDate(date) {
      this.date = date;
    },
    getLocation() {
      return this.coordinates;
    },
    setLocation(coordinates) {
      this.coordinates = coordinates;
    },
    setCancelAlert(cancelAlert) {
      this.cancelAlert = cancelAlert;
    },
    getCancelAlert() {
      return this.cancelAlert;
    },
    isSending() {
      return this.sending;
    },
    setSending(state) {
      this.sending = state;
    },
    isMinimalObservationContentSet() {
      return (
        this.ObservationData.isMinimalContentSet() ||
        this.DocumentData.isMinimalAttachmentContentSet()
      );
    },
  });
}
export default createStore();

function getGeoJsonLocation(store) {
  return {
    coordinates: [store.getLocation().lng, store.getLocation().lat],
    type: "Point",
  };
}

function nullToUndefined(value) {
  return value != null ? value : undefined;
}

function nullOrEmptyStringToUndefined(value) {
  return value != null && value !== "" ? value : undefined;
}

function toNumber(value) {
  return value != null && value !== "" ? Number(value) : undefined;
}

function getContactInfo(store) {
  const firstName = nullOrEmptyStringToUndefined(
    store.PersonalData.getFirstName()
  );
  const lastName = nullOrEmptyStringToUndefined(
    store.PersonalData.getLastName()
  );
  const phone = nullOrEmptyStringToUndefined(store.PersonalData.getPhone());
  const email = nullOrEmptyStringToUndefined(store.PersonalData.getMail());
  const education = nullToUndefined(store.PersonalData.getEducation());
  return {
    firstName,
    lastName,
    phone,
    email,
    education,
  };
}

function getAvalancheVictims(store) {
  const personDamageExists = store.AvalancheData.getPersonDamageExists();
  if (!personDamageExists) {
    return undefined;
  }
  const victims = store.AvalancheData.getVictims();
  if (victims.length === 0) {
    return undefined;
  }
  return victims.map((victim) => {
    console.log(victim);
    return {
      personDamageType: nullToUndefined(victim.personDamageType),
      burialType: nullToUndefined(victim.burialType),
      burialPeriod: toNumber(victim.burialPeriod),
      burialDepth: toNumber(victim.burialDepth),
    };
  });
}

function getDangerSign(store) {
  const snowdriftSize = nullToUndefined(
    store.ObservationData.getSnowdriftSize()
  );
  // eslint-disable-next-line max-len
  const whumpfCracksFrequency = nullToUndefined(
    store.ObservationData.getWhumpfCracksFrequency()
  );
  if (snowdriftSize || whumpfCracksFrequency) {
    return {
      snowdriftSize,
      whumpfCracksFrequency,
    };
  }
  return undefined;
}

// eslint-disable-next-line complexity
function getDanger(store) {
  const problem = nullToUndefined(store.ObservationData.getDangerOf());
  const aspectFrom = nullToUndefined(store.ObservationData.getAspectFrom());
  const aspectTo = nullToUndefined(store.ObservationData.getAspectTo());
  const altitudeAbove = nullToUndefined(
    store.ObservationData.getAltitudeAbove()
  );
  const altitudeValue = nullToUndefined(store.ObservationData.getAltitude());
  const level = nullToUndefined(store.ObservationData.getLevel());
  if (
    problem ||
    aspectFrom ||
    aspectTo ||
    altitudeAbove ||
    altitudeValue ||
    level
  ) {
    return {
      problem,
      aspectFrom,
      aspectTo,
      altitudeAbove,
      altitudeValue,
      level,
    };
  }
  return undefined;
}

function getAttachment(store) {
  const comment = nullOrEmptyStringToUndefined(store.DocumentData.getComment());
  const confidential = !!store.DocumentData.getInternalUse();
  if (comment || confidential) {
    return {
      comment,
      confidential,
    };
  }
  return undefined;
}

function getUploadedDocuments(store) {
  const documents = store.DocumentData.getUploadedFiles();
  if (documents instanceof Map && documents.size > 0) {
    return Array.from(documents, ([, file]) => file);
  } else {
    return [];
  }
}

export function getAvalancheReport(store) {
  return {
    date: store.getDate(),
    location: getGeoJsonLocation(store),
    contactInfo: getContactInfo(store),
    documents: getUploadedDocuments(store),
    comment: nullOrEmptyStringToUndefined(store.DocumentData.getComment()),
    confidential: !!store.DocumentData.getInternalUse(),
    avalancheSize:
      store.AvalancheData.getAvalancheSize() || AvalancheSize.Unknown,
    avalancheType:
      store.AvalancheData.getAvalancheType() || AvalancheType.Unknown,
    triggerType: store.AvalancheData.getTriggerType() || TriggerType.Unknown,
    avalancheMoisture:
      store.AvalancheData.getAvalancheMoisture() || AvalancheMoisture.Unknown,
    triggerRemote: nullToUndefined(store.AvalancheData.getTriggerRemote()),
    materialDamage: nullToUndefined(
      store.AvalancheData.getMaterialDamageExists()
    ),
    personDamage: nullToUndefined(store.AvalancheData.getPersonDamageExists()),
    avalancheVictims: getAvalancheVictims(store),
  };
}

export function getObservationReport(store) {
  return {
    date: store.date,
    location: getGeoJsonLocation(store),
    contactInfo: getContactInfo(store),
    documents: getUploadedDocuments(store),
    dangersign: getDangerSign(store),
    danger: getDanger(store),
    attachment: getAttachment(store),
  };
}
